import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Spin, Typography } from "@arco-design/web-react";

import { useFetchVersionDetail } from "../../api/hooks/useFetchVersion";
import { useCurrentLanguage } from "../../utils/hooks/useCurrentLanguage";
import { formatReleaseDate } from "./versionPageUtils";
import { useVersionNavigation, VersionPageShell } from "./VersionPageShell";
import "./Version.css";

export const VersionDetail = () => {
  const { t } = useTranslation();
  const currentLanguage = useCurrentLanguage();
  const { version: versionParam } = useParams<{ version: string }>();
  const { backToList } = useVersionNavigation();
  const { data, isLoading, isError, refetch } = useFetchVersionDetail(versionParam);
  const [categoryIndex, setCategoryIndex] = useState(0);

  const categories = useMemo(() => {
    if (!data?.changelog) {
      return [];
    }
    return Object.entries(data.changelog);
  }, [data?.changelog]);

  const currentCategory = categories[categoryIndex];
  const totalCategories = categories.length;

  return (
    <VersionPageShell onBack={backToList}>
      {isLoading && (
        <div className="version-page__loading">
          <Spin size={32} />
        </div>
      )}

      {isError && (
        <div className="version-page__error">
          <p>{t("version_page.error_message")}</p>
          <Button className="version-page__retry" onClick={() => refetch()}>
            {t("version_page.retry_button")}
          </Button>
          <Button
            type="text"
            style={{ marginTop: 12, display: "block" }}
            onClick={backToList}
          >
            {t("version_page.back_to_history")}
          </Button>
        </div>
      )}

      {data && !isLoading && (
        <>
          <div className="version-page__hero">
            <Typography.Title heading={3} className="version-page__version">
              v{data.version}
            </Typography.Title>
            {data.releaseDate && (
              <Typography.Text className="version-page__date">
                {formatReleaseDate(data.releaseDate, currentLanguage)}
              </Typography.Text>
            )}
          </div>

          {currentCategory && (
            <div className="version-page__content">
              <Typography.Title heading={5} className="version-page__category">
                {currentCategory[0]}
              </Typography.Title>
              <ul className="version-page__list">
                {currentCategory[1].map((item) => (
                  <li key={item} className="version-page__list-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {totalCategories > 1 && (
            <div className="version-page__footer">
              <button
                type="button"
                className="version-page__nav-btn version-page__nav-btn--prev"
                disabled={categoryIndex === 0}
                onClick={() => setCategoryIndex((index) => index - 1)}
                aria-label={t("version_page.previous_category")}
              >
                ← <span className="version-page__nav-label">{t("version_page.previous_category")}</span>
              </button>
              <span className="version-page__counter">
                {t("version_page.category_of", {
                  current: categoryIndex + 1,
                  total: totalCategories,
                })}
              </span>
              <button
                type="button"
                className="version-page__nav-btn version-page__nav-btn--next"
                disabled={categoryIndex >= totalCategories - 1}
                onClick={() => setCategoryIndex((index) => index + 1)}
                aria-label={t("version_page.next_category")}
              >
                <span className="version-page__nav-label">{t("version_page.next_category")}</span> →
              </button>
            </div>
          )}
        </>
      )}
    </VersionPageShell>
  );
};
